import React, { useEffect, useState } from "react";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken, getDecryptedData } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, MenuItem, Select, InputLabel, FormControl, Chip, Grid,
  CircularProgress, Typography, Snackbar, Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { useApiContext } from "../../../Utils/ApiContext/ApiContext";
import { useNavigate } from "react-router-dom";

const ApprovedAppointments = ({ onAppointmentSelect }) => {
  const { doctors, fetchData } = useApiContext();
  const accessToken = getDecryptedAccessToken();
  const LoginUserData = getDecryptedData();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assigningDoctor, setAssigningDoctor] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
const [selectedAppointment, setSelectedAppointment] = useState(null);
const [notes, setNotes] = useState({});
const [savingNotes, setSavingNotes] = useState({});
const navigate = useNavigate()
const fetchNewRequests = async () => {
  setLoading(true);
  setError("");
  try {
    const response = await ApiServices.get(
      "/apis/default/api/appointmentBooked/approved",
      { Authorization: `Bearer ${accessToken}` }
    );

    fetchData();
    if (!response.data || response.data.length === 0) {
      setError("No new appointment requests available.");
      setAppointments([]);
      setFilteredAppointments([]);
    } else {
      setError("");
      setAppointments(response.data);
      setFilteredAppointments(response.data);

      const types = [...new Set(response.data.map((item) => item.appointment_type))];
      setAppointmentTypes(types);
    }
  } catch (error) {
    console.error("Error fetching new requests:", error);
    setError("Failed to fetch appointment requests. Please try again.");
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchNewRequests();
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    filterData(query, selectedType);
  };

  const handleTypeFilter = (event) => {
    const type = event.target.value;
    setSelectedType(type);
    filterData(searchQuery, type);
  };

  const filterData = (query, type) => {
    let filtered = appointments;

    if (query) {
      filtered = filtered.filter(
        (item) =>
          item.pc_eid.includes(query) ||
          item.patient_fname.toLowerCase().includes(query) ||
          item.patient_lname.toLowerCase().includes(query)
      );
    }

    if (type) {
      filtered = filtered.filter((item) => item.appointment_type === type);
    }

    setFilteredAppointments(filtered);
  };

  const handleAssignDoctor = async (appointment, doctorId) => {
    if (!doctorId) return;

    setAssigningDoctor((prev) => ({ ...prev, [appointment.pc_eid]: true }));

    try {
      // 1. **Update the appointment with the assigned doctor**
      const updateAppointmentResponse = await ApiServices.put(
        `/apis/default/api/patient/${appointment.pc_pid}/appointment/${appointment.pc_eid}`,
        {
          ...appointment,
          pc_aid : parseInt(doctorId, 10),
          pc_apptstatus: "APPR",
          pc_facility: "wellness",
          assigned_by: LoginUserData.id,
        },
        { Authorization: `Bearer ${accessToken}` }
      );

      if (!updateAppointmentResponse.data.Success) {
        throw new Error("Failed to assign doctor");
      }
      
      await ApiServices.post(
        "/apis/default/api/sendFormsPatient",
        {
          pid: appointment.pc_pid,
          eid: appointment.pc_eid,
          form_id: appointment.service_id,
        },
        { Authorization: `Bearer ${accessToken}` }
      );

      // 3. **Update appointment status**
      await ApiServices.put(
        "/apis/default/api/apptStatusUpdate",
        {
          pid: appointment.pc_pid,
          encounter_id: appointment.pc_eid,
          apptstatus: "APPR",
        },
        { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
      );

      // **Success Message**
      setSnackbar({ open: true, message: "Doctor assigned successfully!", severity: "success" });

      fetchNewRequests(); // Refresh doctor list
    } catch (error) {
      console.error("Error assigning doctor:", error);
      setSnackbar({ open: true, message: "Failed to assign doctor. Try again.", severity: "error" });
    } finally {
      setAssigningDoctor((prev) => ({ ...prev, [appointment.pc_eid]: false }));
    }
  };

  const handleRejectClick = (appointment) => {
    setSelectedAppointment(appointment);
    setRejectDialogOpen(true);
  };
  
  const handleConfirmReject = async () => {
    if (!selectedAppointment) return;
    try {
      await ApiServices.put(
        `/apis/default/api/patient/${selectedAppointment.pc_pid}/appointment/${selectedAppointment.pc_eid}`,
        {
          ...selectedAppointment,
          pc_apptstatus: "REJ",
          assigned_by: LoginUserData.id,
        },
        { Authorization: `Bearer ${accessToken}` }
      );
  
      setSnackbar({ open: true, message: "Appointment rejected successfully!", severity: "success" });
      fetchNewRequests();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to reject appointment.", severity: "error" });
    } finally {
      setRejectDialogOpen(false);
    }
  };
  
  const handleNoteChange = (appointmentId, value) => {
    setNotes((prev) => ({ ...prev, [appointmentId]: value }));
  };
  
  const handleSaveNote = async (appointment) => {
    const noteText = notes[appointment.pc_eid] || "";
    if (!noteText.trim()) return;
  
    setSavingNotes((prev) => ({ ...prev, [appointment.pc_eid]: true }));
  
    try {
      await ApiServices.put(
        `/apis/default/api/patient/${appointment.pc_pid}/appointment/${appointment.pc_eid}`,
        {
          ...appointment,
          soap_note: noteText,
        },
        { Authorization: `Bearer ${accessToken}` }
      );
  
      setSnackbar({ open: true, message: "Note saved successfully!", severity: "success" });
      fetchNewRequests();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to save note.", severity: "error" });
    } finally {
      setSavingNotes((prev) => ({ ...prev, [appointment.pc_eid]: false }));
    }
  };
  
  const consultationView = (appointment) => {
  const url = `/provider/consultation/${appointment.zoom_meeting_id}/${appointment.zoom_meeting_password}`;
  window.open(url, '_blank');
};


  return (
    <Grid container spacing={2} sx={{ padding: 2 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Search by Appointment ID or Patient Name"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Filter by Appointment Type</InputLabel>
          <Select value={selectedType} onChange={handleTypeFilter}>
            <MenuItem value="">All</MenuItem>
            {appointmentTypes.map((type, index) => (
              <MenuItem key={index} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        {loading ? (
          <Grid container justifyContent="center" sx={{ marginTop: 3 }}>
            <CircularProgress />
          </Grid>
        ) : error ? (
          <Typography variant="h6" align="center" color="error">
            {error}
          </Typography>
        ) : (
          <TableContainer component={Paper}>
          <Table>
  <TableHead>
    <TableRow>
      <TableCell><b>Appointment ID</b></TableCell>
      <TableCell><b>Type</b></TableCell>
      <TableCell><b>Date</b></TableCell>
      <TableCell><b>Time</b></TableCell>
      <TableCell><b>Name</b></TableCell>
      <TableCell><b>Contact</b></TableCell>
      <TableCell><b>Doctor</b></TableCell>
      <TableCell><b>Assign Doctor</b></TableCell>
      <TableCell><b>Reject</b></TableCell>
      <TableCell><b>Join Online</b></TableCell>
      <TableCell><b>Note</b></TableCell>
    </TableRow>
  </TableHead>
  <TableBody>
    {filteredAppointments.map((appointment) => (
      <TableRow key={appointment.pc_eid} hover onClick={() => onAppointmentSelect(appointment)}>
        <TableCell>{appointment.pc_eid}</TableCell>
        <TableCell>{appointment.appointment_type}</TableCell>
        <TableCell>
          <CalendarTodayIcon color="primary" /> {appointment.pc_eventDate}
        </TableCell>
        <TableCell>
          <AccessTimeIcon color="secondary" /> {appointment.pc_startTime}
        </TableCell>
        <TableCell>
          {appointment.patient_fname} {appointment.patient_lname}
        </TableCell>
        <TableCell>
          <PhoneIcon color="success" /> {appointment.patient_contact}
        </TableCell>
        <TableCell>
          {appointment.provider_fname === "N/A" ? (
            <Chip icon={<PersonOffIcon />} label="Not Assigned" color="warning" />
          ) : (
            `${appointment.provider_fname} ${appointment.provider_lname}`
          )}
        </TableCell>
        
        {/* Assign Doctor Dropdown (Prevent row click) */}
        <TableCell>
          <FormControl fullWidth>
            <Select
              displayEmpty
              onClick={(e) => e.stopPropagation()} // ⛔ Prevent row click
              onChange={(e) => handleAssignDoctor(appointment, e.target.value)}
              disabled={assigningDoctor[appointment.pc_eid]}
            >
              <MenuItem value="">Assign Doctor</MenuItem>
              {doctors.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.fname} {doctor.lname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        
        <TableCell>
  {appointment.pc_apptstatus === "REJ" ? (
    <Chip label="Rejected" color="error" />
  ) : (
    <Button
      onClick={(e) => {
        e.stopPropagation(); // ⛔ Prevent row click
        handleRejectClick(appointment);
      }}
      style={{ color: "red" }}
      disabled={appointment.pc_apptstatus === "REJ"} // ✅ Disable if already rejected
    >
      Reject
    </Button>
  )}
</TableCell>
 <TableCell>
          <Button
  variant="contained"
  color="primary"
  disabled={appointment.is_virtual !== true}
  onClick={(e) => {
    e.stopPropagation(); // Prevent row click
    consultationView(appointment); // Handle navigation in new tab
  }}
>
  Join Consultation
</Button>

        </TableCell>
        {/* SOAP Note (Prevent row click) */}
        <TableRow>
          <TableCell colSpan={8}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="SOAP Note"
              variant="outlined"
              value={notes[appointment.pc_eid] || appointment.soap_note || ""}
              onClick={(e) => e.stopPropagation()} // ⛔ Prevent row click
              onChange={(e) => handleNoteChange(appointment.pc_eid, e.target.value)}
            />
            <button 
              onClick={(e) => {
                e.stopPropagation(); // ⛔ Prevent row click
                handleSaveNote(appointment);
              }} 
              disabled={savingNotes[appointment.pc_eid]}
            >
              {savingNotes[appointment.pc_eid] ? "Saving..." : "Save Note"}
            </button>
          </TableCell>
        </TableRow>

      </TableRow>
    ))}
   
  </TableBody>
</Table>

          </TableContainer>
        )}
      </Grid>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
  <DialogTitle>Confirm Rejection</DialogTitle>
  <DialogContent>
    <Typography>
      Are you sure you want to reject this appointment?
    </Typography>
    <Typography><b>Patient:</b> {selectedAppointment?.patient_fname} {selectedAppointment?.patient_lname}</Typography>
    <Typography><b>Date:</b> {selectedAppointment?.pc_eventDate}</Typography>
    <Typography><b>Time:</b> {selectedAppointment?.pc_startTime}</Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
    <Button onClick={handleConfirmReject} color="error">Reject</Button>
  </DialogActions>
</Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Grid>
  );
};

export default ApprovedAppointments;
